import React, { createContext, useContext, useState } from 'react';
import { Navbar as BSNavbar } from 'react-bootstrap';
import cn from 'classnames';
import invariant from 'tiny-invariant';

import Link from '../../../components/Link/Link';
import Icon from '../../../components/icon/Icon';
import routes from '../../../routes/constants';
import UserNav from './UserNav';
import ProjectSelect from './ProjectSelect';
import ViewModes from './ViewModes';
import RealtimeUsers from './RealtimeUsers';
import CartButton from './CartButton';
import { useDispatch, useSelector } from 'react-redux';
import text from '../../../text';
import { toggleSidenavVisibility } from '../../../redux/actions';
import useBreakpoint from '../../../hooks/useBreakpoint';

const NavBarContext = createContext<{
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
}>(null!);

export const useNavBarContext = () => {
  const ctx = useContext(NavBarContext);
  invariant(ctx, 'missing NavBarContext.Provider');
  return ctx;
};

const Provider = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <NavBarContext.Provider value={{ isExpanded, setIsExpanded }}>
      <BSNavbar
        expanded={isExpanded}
        onToggle={setIsExpanded}
        expand='lg'
        variant='dark'
        className={cn('Navbar', className)}
      >
        {children}
      </BSNavbar>
    </NavBarContext.Provider>
  );
};

const Brand = () => {
  const isPhoneOrTablet = useBreakpoint(['xs', 'sm', 'md']);
  return (
    <BSNavbar.Brand className='Navbar-Brand'>
      <Link to={routes.home} className='d-inline-flex'>
        {isPhoneOrTablet ? (
          <Icon icon='larki-icon' />
        ) : (
          <Icon icon='larki-logo' />
        )}
      </Link>
    </BSNavbar.Brand>
  );
};

const Toggle = () => {
  const dispatch = useDispatch();
  const { layout } = useSelector(
    (state: {
      layout: {
        sideNav: {
          isVisible: boolean;
        };
      };
    }) => state
  );

  return (
    <BSNavbar.Toggle
      id='menuButton'
      aria-controls='basic-navbar-nav'
      className='Navbar-Toggle'
      onClick={(evt) => {
        dispatch(toggleSidenavVisibility(!layout.sideNav.isVisible));
        evt.stopPropagation();
      }}
    >
      {layout.sideNav.isVisible ? (
        <Icon icon='close-box' />
      ) : (
        <Icon icon='menu' />
      )}
      {text('menu')}
    </BSNavbar.Toggle>
  );
};

const Title = ({ title }: { title: string }) => {
  return <BSNavbar.Brand className='Titlebar'>{title}</BSNavbar.Brand>;
};

const NavBar = {
  Provider,
  Brand,
  UserNav,
  ProjectSelect,
  ViewModes,
  RealtimeUsers,
  CartButton,
  Toggle,
  Title,
};

export default NavBar;
